import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { EASE } from "@/components/Motion";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/employers", label: "For Employers" },
  { to: "/candidates", label: "For Candidates" },
  { to: "/solutions", label: "Solutions" },
  { to: "/industries", label: "Industries" },
  { to: "/jobs", label: "Jobs" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export const LogoImage = ({ className = "", alt = "NEXREQ — Your People Partner in Every Hire", ...props }) => (
  <img
    src="/brand/nexreq-logo-transparent.png"
    alt={alt}
    width={2522}
    height={816}
    decoding="async"
    draggable={false}
    className={`${className} w-auto max-w-none select-none object-contain`}
    {...props}
  />
);

export const Logo = ({ className = "h-12 sm:h-14" }) => (
  <Link to="/" className="flex shrink-0 items-center" data-testid="nav-logo" aria-label="NEXREQ — Home">
    <LogoImage className={className} loading="eager" fetchPriority="high" />
  </Link>
);

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-black/70 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="nx-container flex h-20 items-center justify-between">
          <Logo />
          <nav className="hidden items-center gap-6 xl:flex" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[12.5px] tracking-wide transition-colors duration-300 ${
                    isActive ? "text-emerald-400" : "text-white/65 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 xl:flex">
            <Link
              to="/jobs"
              data-testid="nav-find-jobs-btn"
              className="border border-white/20 px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.12em] text-white transition-colors hover:border-emerald-500 hover:text-emerald-400"
            >
              Find Jobs
            </Link>
            <Link
              to="/hire-talent"
              data-testid="nav-hire-talent-btn"
              className="bg-emerald-600 px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.12em] text-white transition-colors hover:bg-emerald-700"
            >
              Hire Talent
            </Link>
          </div>
          <button
            className="text-white xl:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open-btn"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-obsidian"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="mobile-menu"
          >
            <div className="nx-container flex h-20 items-center justify-between">
              <Logo />
              <button
                className="text-white"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="nx-container flex flex-1 flex-col justify-center gap-1 overflow-y-auto py-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-2 font-heading text-3xl font-light tracking-tight transition-colors sm:text-4xl ${
                        isActive ? "text-emerald-400" : "text-white/80 hover:text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              className="nx-container flex flex-col gap-3 pb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
            >
              <Link to="/hire-talent" className="nx-btn-primary w-full" data-testid="mobile-hire-talent-btn">
                Hire Talent <ArrowRight size={15} />
              </Link>
              <Link to="/jobs" className="nx-btn-outline w-full" data-testid="mobile-find-jobs-btn">
                Find Jobs
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
